import { useState } from 'react';
import { toast } from 'sonner';
import { Mic2, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { ApproveBadge, CreditPill, GenerateStillButton } from '../AIStudioShared';
import { LiveMedia } from './LiveMedia';
import { MotionPref } from './BudgetEstimate';
import { ShotFields, shotFromScene } from './ShotFields';
import { isRendering } from './useMovieProject';
import {
  absUrl, approveStill, errMsg, generateStill, patchScene, removeScene, renderScene, sceneVoiceover,
} from '../../lib/aiStudioApi';

const STAGE = { still: 'Storyboarding', still_ready: 'Storyboard ready', rendering: 'Rendering', ready: 'Ready', failed: 'Needs attention' };

function EditShotDialog({ open, onOpenChange, project, scene, catalog, voices, onSaved }) {
  const [draft, setDraft] = useState(() => shotFromScene(scene));
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try { await patchScene(project.id, scene.id, draft); toast.success('Shot updated'); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-dark max-h-[90vh] max-w-5xl overflow-y-auto border-neutral-800 bg-neutral-950 text-neutral-100 [&>*]:min-w-0" data-testid={`shot-edit-dialog-${scene.id}`}>
        <DialogHeader><DialogTitle>Edit shot</DialogTitle></DialogHeader>
        <ShotFields project={project} catalog={catalog} voices={voices} value={draft} onChange={setDraft} testPrefix="edit-shot" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="edit-shot-save">{saving ? 'Saving…' : 'Save shot'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SceneCard({ project, scene, index, catalog, voices, resolution, previewCredits, onChanged }) {
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const cine = scene.cinematography || {};
  const chips = [cine.camera_movement, cine.shot_size, cine.camera_angle, cine.camera_body, cine.lens, cine.focal_length, cine.film_look, cine.lighting].filter(Boolean);
  const rendering = isRendering(scene);
  const budget = project.engine_effective === 'budget';
  const living = scene.motion_mode === 'still' && !scene.video_url;
  const setPref = (motion_pref) => run('pref', () => patchScene(project.id, scene.id, { motion_pref }),
    motion_pref === 'still' ? 'Kept as a free Living Still' : motion_pref === 'motion' ? 'This shot will always be animated' : 'Budget Director decides');

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  const still = () => run('still', () => generateStill(project.id, scene.id, {
    prompt: scene.still_prompt || scene.description || scene.title, aspect_ratio: project.aspect_ratio, anchor_asset_ids: scene.anchor_asset_ids || [],
  }), scene.still_url ? 'Still revised' : 'Storyboard still ready');
  const render = () => {
    if (!window.confirm(`Render this shot at ${resolution} for ${previewCredits ?? '?'} credits?`)) return;
    run('render', () => renderScene(project.id, scene.id, { resolution }), 'Shot sent to the render farm — the preview updates live');
  };

  return (
    <div className="grid gap-4 rounded-xl border border-neutral-800 bg-neutral-950/60 p-4 lg:grid-cols-[minmax(0,340px)_1fr]" data-testid={`movie-shot-${scene.id}`}>
      <LiveMedia scene={scene} aspect={project.aspect_ratio} busyStill={busy === 'still'} busyRender={busy === 'render'} testId={`movie-shot-media-${scene.id}`} />
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-neutral-800 font-mono text-neutral-200">EP{scene.episode || 1} · {String(index + 1).padStart(2, '0')}</Badge>
            <span className="text-xs text-neutral-500">{scene.duration_seconds}s</span>
            {scene.stage && <Badge className="bg-neutral-900 text-[10px] text-neutral-400" data-testid={`movie-shot-stage-${scene.id}`}>{STAGE[scene.stage] || scene.stage}</Badge>}
            <ApproveBadge approved={scene.still_approved} />
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-7 text-neutral-400" data-testid={`movie-shot-edit-${scene.id}`}><Pencil size={13} /></Button>
            <Button size="sm" variant="ghost" onClick={() => run('remove', () => removeScene(project.id, scene.id), 'Shot removed')}
              className="h-7 text-neutral-500 hover:text-red-300" data-testid={`movie-shot-remove-${scene.id}`}><Trash2 size={13} /></Button>
          </div>
        </div>
        <div>
          {scene.title && <div className="text-sm font-semibold text-neutral-100">{scene.title}</div>}
          <p className="text-sm text-neutral-300">{scene.description}</p>
        </div>
        {(scene.dialogue || scene.narration) && (
          <div className="space-y-1 rounded-md border-l-2 border-amber-400/50 bg-neutral-900/60 px-3 py-2 text-xs">
            {scene.dialogue && <div><span className="font-semibold uppercase tracking-wide text-amber-200">{scene.speaker || 'Character'}:</span> <span className="text-neutral-200">“{scene.dialogue}”</span>
              {scene.dialogue_en && scene.dialogue_en !== scene.dialogue && <span className="text-neutral-500"> — {scene.dialogue_en}</span>}</div>}
            {scene.narration && <div className="italic text-neutral-400">Narrator: {scene.narration}</div>}
            {scene.audio_url && <audio controls src={absUrl(scene.audio_url)} className="mt-1 h-8 w-full" data-testid={`movie-shot-audio-${scene.id}`} />}
          </div>
        )}
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid={`movie-shot-cine-${scene.id}`}>
            {chips.map((c) => <span key={c} className="rounded-full border border-neutral-800 bg-neutral-900 px-2 py-0.5 text-[10px] text-neutral-400">{c}</span>)}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <GenerateStillButton onClick={still} loading={busy === 'still'} revision={!!scene.still_url} data-testid={`movie-shot-still-${scene.id}`} />
          {scene.still_url && !scene.still_approved && (
            <Button size="sm" variant="outline" onClick={() => run('approve', () => approveStill(project.id, scene.id), 'Storyboard approved')}
              className="border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`movie-shot-approve-${scene.id}`}>Approve</Button>
          )}
          <Button size="sm" onClick={render} disabled={!scene.still_approved || busy === 'render' || rendering}
            className="bg-neutral-100 text-neutral-900 hover:bg-white disabled:opacity-40" data-testid={`movie-shot-render-${scene.id}`}>
            <Sparkles size={14} className="mr-1" />
            {rendering ? 'Rendering…' : scene.video_url ? `Re-render — ${previewCredits ?? '…'} cr` : `${living ? 'Animate' : 'Render'} — ${previewCredits ?? '…'} cr`}
          </Button>
          {(scene.dialogue || scene.narration) && (
            <Button size="sm" variant="outline" disabled={busy === 'voice'} onClick={() => run('voice', () => sceneVoiceover(project.id, scene.id), 'Voice-over recorded')}
              className="border-neutral-700 text-neutral-300" data-testid={`movie-shot-voiceover-${scene.id}`}>
              <Mic2 size={13} className="mr-1" /> {busy === 'voice' ? 'Recording…' : scene.audio_url ? 'Re-record voice' : 'Voice-over'}
            </Button>
          )}
          <CreditPill cost={previewCredits ?? '…'} label="motion" data-testid={`movie-shot-cost-${scene.id}`} />
          {budget && <MotionPref value={scene.motion_pref} onChange={setPref} testId={`movie-shot-motion-pref-${scene.id}`} />}
        </div>
      </div>
      {editing && <EditShotDialog open={editing} onOpenChange={setEditing} project={project} scene={scene} catalog={catalog} voices={voices} onSaved={onChanged} />}
    </div>
  );
}
